import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
  Plus, Play, CheckCircle, XCircle, Clock, Loader2, Search, MoreVertical,
  IndianRupee, Users, FileText, Send, Ban, CreditCard, Eye, Edit, Trash2,
  ChevronRight, AlertCircle
} from 'lucide-react';

const months = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' },
  { value: 3, label: 'March' }, { value: 4, label: 'April' },
  { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' },
  { value: 9, label: 'September' }, { value: 10, label: 'October' },
  { value: 11, label: 'November' }, { value: 12, label: 'December' }
];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 3 }, (_, i) => currentYear - i + 1);

const statusConfig = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-800', icon: FileText },
  submitted: { label: 'Pending Approval', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  processing: { label: 'Processing', color: 'bg-blue-100 text-blue-800', icon: Loader2 },
  paid: { label: 'Paid', color: 'bg-emerald-100 text-emerald-800', icon: CreditCard },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800', icon: XCircle }
};

export default function PayrollRunPage() {
  const { user } = useAuth();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterYear, setFilterYear] = useState(currentYear);
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  
  // Selected items
  const [selectedRun, setSelectedRun] = useState(null);
  const [runItems, setRunItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Forms
  const [createForm, setCreateForm] = useState({
    month: new Date().getMonth() + 1,
    year: currentYear,
    description: ''
  });
  const [overrideForm, setOverrideForm] = useState({});
  const [rejectReason, setRejectReason] = useState('');
  
  // Search in items
  const [itemSearch, setItemSearch] = useState('');

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    try {
      const params = { year: filterYear };
      if (filterStatus !== 'all') params.status = filterStatus;
      
      const res = await api.get('/payroll/runs', { params });
      setRuns(res.data.runs || []);
    } catch (error) {
      toast.error('Failed to fetch payroll runs');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterYear]);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  const fetchRunItems = async (runId) => {
    setItemsLoading(true);
    try {
      const res = await api.get(`/payroll/runs/${runId}/items`, { params: { limit: 200 } });
      setRunItems(res.data.items || []);
    } catch (error) {
      toast.error('Failed to fetch payroll items');
    } finally {
      setItemsLoading(false);
    }
  };

  const handleCreateRun = async () => {
    setSubmitting(true);
    try {
      await api.post('/payroll/runs', {
        ...createForm,
        include_all_active: true
      });
      toast.success('Payroll run created successfully');
      setShowCreateDialog(false);
      setCreateForm({ month: new Date().getMonth() + 1, year: currentYear, description: '' });
      fetchRuns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payroll run');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewRun = async (run) => {
    setSelectedRun(run);
    setShowViewDialog(true);
    await fetchRunItems(run.id);
  };

  const handleSubmitRun = async (runId) => {
    try {
      await api.post(`/payroll/runs/${runId}/submit`);
      toast.success('Payroll run submitted for approval');
      fetchRuns();
      if (selectedRun?.id === runId) {
        const res = await api.get(`/payroll/runs/${runId}`);
        setSelectedRun(res.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit payroll run');
    }
  };

  const handleApproveRun = async (runId) => {
    try {
      await api.post(`/payroll/runs/${runId}/approve`);
      toast.success('Payroll run approved');
      fetchRuns();
      if (selectedRun?.id === runId) {
        const res = await api.get(`/payroll/runs/${runId}`);
        setSelectedRun(res.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve payroll run');
    }
  };

  const handleRejectRun = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    try {
      await api.post(`/payroll/runs/${selectedRun.id}/reject`, null, {
        params: { reason: rejectReason }
      });
      toast.success('Payroll run rejected');
      setShowRejectDialog(false);
      setRejectReason('');
      fetchRuns();
      setShowViewDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject payroll run');
    }
  };

  const handleMarkProcessing = async (runId) => {
    try {
      await api.post(`/payroll/runs/${runId}/process`);
      toast.success('Payroll marked as processing');
      fetchRuns();
      if (selectedRun?.id === runId) {
        const res = await api.get(`/payroll/runs/${runId}`);
        setSelectedRun(res.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update payroll status');
    }
  };

  const handleMarkPaid = async (runId) => {
    try {
      await api.post(`/payroll/runs/${runId}/paid`);
      toast.success('Payroll marked as paid');
      fetchRuns();
      if (selectedRun?.id === runId) {
        const res = await api.get(`/payroll/runs/${runId}`);
        setSelectedRun(res.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark payroll as paid');
    }
  };

  const handleDeleteRun = async (runId) => {
    if (!confirm('Are you sure you want to delete this draft payroll run?')) return;
    try {
      await api.delete(`/payroll/runs/${runId}`);
      toast.success('Payroll run deleted');
      fetchRuns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete payroll run');
    }
  };

  const handleOverrideItem = (item) => {
    setSelectedItem(item);
    setOverrideForm({
      basic_salary: item.basic_salary,
      hra: item.hra,
      special_allowance: item.special_allowance,
      other_earnings: item.other_earnings || 0,
      lop_days: item.lop_days || 0,
      other_deductions: item.other_deductions || 0,
      tds_override: null,
      notes: item.override_notes || ''
    });
    setShowOverrideDialog(true);
  };

  const handleSaveOverride = async () => {
    setSubmitting(true);
    try {
      await api.put(`/payroll/runs/${selectedRun.id}/items/${selectedItem.id}`, overrideForm);
      toast.success('Payroll item updated');
      setShowOverrideDialog(false);
      await fetchRunItems(selectedRun.id);
      // Refresh run totals
      const res = await api.get(`/payroll/runs/${selectedRun.id}`);
      setSelectedRun(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update payroll item');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Stats
  const stats = useMemo(() => {
    const draft = runs.filter(r => r.status === 'draft').length;
    const pending = runs.filter(r => r.status === 'submitted').length;
    const approved = runs.filter(r => r.status === 'approved').length;
    const paid = runs.filter(r => r.status === 'paid').length;
    return { draft, pending, approved, paid };
  }, [runs]);

  // Filtered items
  const filteredItems = useMemo(() => {
    if (!itemSearch) return runItems;
    const search = itemSearch.toLowerCase();
    return runItems.filter(item =>
      item.employee_name?.toLowerCase().includes(search) ||
      item.employee_code?.toLowerCase().includes(search) ||
      item.department?.toLowerCase().includes(search)
    );
  }, [runItems, itemSearch]);

  const canApprove = user?.role === 'super_admin';
  const canProcess = ['super_admin', 'admin', 'accounts'].includes(user?.role);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Payroll Runs</h1>
          <p className="text-muted-foreground mt-1">Manage monthly payroll processing</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Create Payroll Run
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card 
          className={`cursor-pointer hover:shadow-md transition-all ${filterStatus === 'draft' ? 'ring-2 ring-primary' : ''}`}
          onClick={() => setFilterStatus(filterStatus === 'draft' ? 'all' : 'draft')}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <FileText className="w-4 h-4" />
                  <span className="text-sm">Draft</span>
                </div>
                <p className="text-2xl font-bold">{stats.draft}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer hover:shadow-md transition-all ${filterStatus === 'submitted' ? 'ring-2 ring-primary' : ''}`}
          onClick={() => setFilterStatus(filterStatus === 'submitted' ? 'all' : 'submitted')}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 text-yellow-600 mb-1">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">Pending Approval</span>
                </div>
                <p className="text-2xl font-bold">{stats.pending}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer hover:shadow-md transition-all ${filterStatus === 'approved' ? 'ring-2 ring-primary' : ''}`}
          onClick={() => setFilterStatus(filterStatus === 'approved' ? 'all' : 'approved')}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 text-green-600 mb-1">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm">Approved</span>
                </div>
                <p className="text-2xl font-bold">{stats.approved}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer hover:shadow-md transition-all ${filterStatus === 'paid' ? 'ring-2 ring-primary' : ''}`}
          onClick={() => setFilterStatus(filterStatus === 'paid' ? 'all' : 'paid')}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 text-emerald-600 mb-1">
                  <CreditCard className="w-4 h-4" />
                  <span className="text-sm">Paid</span>
                </div>
                <p className="text-2xl font-bold">{stats.paid}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={String(filterYear)} onValueChange={(v) => setFilterYear(Number(v))}>
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Year" />
          </SelectTrigger>
          <SelectContent>
            {years.map(y => (
              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {filterStatus !== 'all' && (
          <Button variant="ghost" size="sm" onClick={() => setFilterStatus('all')}>
            Clear Filter
          </Button>
        )}
      </div>

      {/* Runs List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Payroll Runs - {filterYear}</CardTitle>
        </CardHeader>
        <CardContent>
          {runs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No payroll runs found for {filterYear}</p>
              <Button variant="outline" className="mt-4" onClick={() => setShowCreateDialog(true)}>
                Create First Run
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Period</TableHead>
                  <TableHead>Employees</TableHead>
                  <TableHead className="text-right">Total Gross</TableHead>
                  <TableHead className="text-right">Total Net</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created By</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.map((run) => {
                  const StatusIcon = statusConfig[run.status]?.icon || FileText;
                  return (
                    <TableRow key={run.id}>
                      <TableCell className="font-medium">
                        {run.month_name} {run.year}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Users className="w-4 h-4 text-muted-foreground" />
                          {run.total_employees}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">{formatCurrency(run.total_gross)}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(run.total_net)}</TableCell>
                      <TableCell>
                        <Badge className={statusConfig[run.status]?.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig[run.status]?.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{run.created_by_name}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewRun(run)}>
                              <Eye className="w-4 h-4 mr-2" /> View Details
                            </DropdownMenuItem>
                            {run.status === 'draft' && (
                              <>
                                <DropdownMenuItem onClick={() => handleSubmitRun(run.id)}>
                                  <Send className="w-4 h-4 mr-2" /> Submit for Approval
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDeleteRun(run.id)} className="text-red-600">
                                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                                </DropdownMenuItem>
                              </>
                            )}
                            {run.status === 'submitted' && canApprove && (
                              <>
                                <DropdownMenuItem onClick={() => handleApproveRun(run.id)}>
                                  <CheckCircle className="w-4 h-4 mr-2" /> Approve
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => { setSelectedRun(run); setShowRejectDialog(true); }} className="text-red-600">
                                  <Ban className="w-4 h-4 mr-2" /> Reject
                                </DropdownMenuItem>
                              </>
                            )}
                            {run.status === 'approved' && canProcess && (
                              <>
                                <DropdownMenuItem onClick={() => handleMarkProcessing(run.id)}>
                                  <Play className="w-4 h-4 mr-2" /> Mark Processing
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleMarkPaid(run.id)}>
                                  <CreditCard className="w-4 h-4 mr-2" /> Mark as Paid
                                </DropdownMenuItem>
                              </>
                            )}
                            {run.status === 'processing' && canProcess && (
                              <DropdownMenuItem onClick={() => handleMarkPaid(run.id)}>
                                <CreditCard className="w-4 h-4 mr-2" /> Mark as Paid
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Payroll Run</DialogTitle>
            <DialogDescription>
              Start a new payroll run for a specific month. All active employees will be included.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Month</Label>
                <Select 
                  value={String(createForm.month)} 
                  onValueChange={(v) => setCreateForm(prev => ({ ...prev, month: Number(v) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map(m => (
                      <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Year</Label>
                <Select 
                  value={String(createForm.year)} 
                  onValueChange={(v) => setCreateForm(prev => ({ ...prev, year: Number(v) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map(y => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Description (Optional)</Label>
              <Input
                placeholder="e.g., Regular monthly payroll"
                value={createForm.description}
                onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateRun} disabled={submitting}>
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Run
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Run Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Payroll Run: {selectedRun?.month_name} {selectedRun?.year}
              <Badge className={statusConfig[selectedRun?.status]?.color}>
                {statusConfig[selectedRun?.status]?.label}
              </Badge>
            </DialogTitle>
            <DialogDescription>
              {selectedRun?.description}
            </DialogDescription>
          </DialogHeader>

          {/* Summary */}
          <div className="grid grid-cols-4 gap-4 my-4">
            <Card className="bg-muted/50">
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground">Employees</p>
                <p className="text-xl font-bold">{selectedRun?.total_employees}</p>
              </CardContent>
            </Card>
            <Card className="bg-green-50">
              <CardContent className="pt-4">
                <p className="text-sm text-green-600">Total Gross</p>
                <p className="text-xl font-bold text-green-700">{formatCurrency(selectedRun?.total_gross)}</p>
              </CardContent>
            </Card>
            <Card className="bg-red-50">
              <CardContent className="pt-4">
                <p className="text-sm text-red-600">Total Deductions</p>
                <p className="text-xl font-bold text-red-700">{formatCurrency(selectedRun?.total_deductions)}</p>
              </CardContent>
            </Card>
            <Card className="bg-primary/10">
              <CardContent className="pt-4">
                <p className="text-sm text-primary">Total Net</p>
                <p className="text-xl font-bold text-primary">{formatCurrency(selectedRun?.total_net)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Items Search */}
          <div className="flex items-center gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search employees..."
                value={itemSearch}
                onChange={(e) => setItemSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Items Table */}
          {itemsLoading ? (
            <Skeleton className="h-64" />
          ) : (
            <div className="border rounded-lg max-h-96 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead className="text-right">Gross</TableHead>
                    <TableHead className="text-right">LOP Days</TableHead>
                    <TableHead className="text-right">Deductions</TableHead>
                    <TableHead className="text-right">Net</TableHead>
                    {selectedRun?.status === 'draft' && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{item.employee_name}</p>
                          <p className="text-xs text-muted-foreground">{item.employee_code}</p>
                        </div>
                        {item.is_overridden && (
                          <Badge variant="outline" className="text-xs mt-1">
                            <AlertCircle className="w-3 h-3 mr-1" /> Modified
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>{item.department}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.gross_salary)}</TableCell>
                      <TableCell className="text-right">
                        {item.lop_days > 0 ? (
                          <span className="text-red-600">{item.lop_days}</span>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-red-600">{formatCurrency(item.total_deductions)}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(item.net_salary)}</TableCell>
                      {selectedRun?.status === 'draft' && (
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => handleOverrideItem(item)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Actions */}
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>Close</Button>
            {selectedRun?.status === 'draft' && (
              <Button onClick={() => handleSubmitRun(selectedRun.id)}>
                <Send className="w-4 h-4 mr-2" /> Submit for Approval
              </Button>
            )}
            {selectedRun?.status === 'submitted' && canApprove && (
              <>
                <Button variant="destructive" onClick={() => setShowRejectDialog(true)}>
                  <Ban className="w-4 h-4 mr-2" /> Reject
                </Button>
                <Button onClick={() => handleApproveRun(selectedRun.id)}>
                  <CheckCircle className="w-4 h-4 mr-2" /> Approve
                </Button>
              </>
            )}
            {selectedRun?.status === 'approved' && canProcess && (
              <Button onClick={() => handleMarkPaid(selectedRun.id)}>
                <CreditCard className="w-4 h-4 mr-2" /> Mark as Paid
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Override Item Dialog */}
      <Dialog open={showOverrideDialog} onOpenChange={setShowOverrideDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Payroll - {selectedItem?.employee_name}</DialogTitle>
            <DialogDescription>
              Modify salary components for this employee
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Basic Salary</Label>
                <Input
                  type="number"
                  value={overrideForm.basic_salary || ''}
                  onChange={(e) => setOverrideForm(prev => ({ ...prev, basic_salary: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <Label>HRA</Label>
                <Input
                  type="number"
                  value={overrideForm.hra || ''}
                  onChange={(e) => setOverrideForm(prev => ({ ...prev, hra: parseFloat(e.target.value) || 0 }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Special Allowance</Label>
                <Input
                  type="number"
                  value={overrideForm.special_allowance || ''}
                  onChange={(e) => setOverrideForm(prev => ({ ...prev, special_allowance: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <Label>Other Earnings</Label>
                <Input
                  type="number"
                  value={overrideForm.other_earnings || ''}
                  onChange={(e) => setOverrideForm(prev => ({ ...prev, other_earnings: parseFloat(e.target.value) || 0 }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>LOP Days</Label>
                <Input
                  type="number"
                  min="0"
                  max="30"
                  value={overrideForm.lop_days || 0}
                  onChange={(e) => setOverrideForm(prev => ({ ...prev, lop_days: parseInt(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <Label>Other Deductions</Label>
                <Input
                  type="number"
                  value={overrideForm.other_deductions || ''}
                  onChange={(e) => setOverrideForm(prev => ({ ...prev, other_deductions: parseFloat(e.target.value) || 0 }))}
                />
              </div>
            </div>
            <div>
              <Label>TDS Override (Optional)</Label>
              <Input
                type="number"
                placeholder="Leave empty to auto-calculate"
                value={overrideForm.tds_override || ''}
                onChange={(e) => setOverrideForm(prev => ({ ...prev, tds_override: e.target.value ? parseFloat(e.target.value) : null }))}
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                placeholder="Reason for override..."
                value={overrideForm.notes || ''}
                onChange={(e) => setOverrideForm(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOverrideDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveOverride} disabled={submitting}>
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Payroll Run</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this payroll run.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Rejection Reason</Label>
            <Textarea
              placeholder="Enter reason for rejection..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleRejectRun}>
              Reject Payroll
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
